import { useEffect, useMemo, useState } from "react";
import { useGame } from "@/context/GameContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ITEM_ICON, SKILL_LABEL, itemName, skillIcon, pctInLevel } from "@/lib/gameData";
import { SkillRing } from "@/components/game/SkillRing";
import {
  ArrowLeft, Play, StopCircle, Sparkles, PawPrint, Coins, Flame,
  ChevronDown, Package, ChevronRight, ScrollText, Trophy,
} from "lucide-react";

const CATEGORY_LABEL = {
  gathering: "Gathering",
  artisan:   "Artisan",
  support:   "Support",
  combat:    "Combat",
};

const SKILL_TAGLINE = {
  woodcutting:  "The forest bends to a patient axe.",
  mining:       "Every vein sings if you listen.",
  fishing:      "Cast wide, reel slow, cook well.",
  farming:      "Roots patiently pay their tenants.",
  hunter:       "Read the trail. Set the trap. Wait.",
  cooking:      "Turn raw fortune into full bellies.",
  firemaking:   "Warmth is a currency too.",
  smithing:     "Ore into intent, hammered true.",
  fletching:    "A cleaner arrow makes a cleaner shot.",
  crafting:     "The world rewards those who make things.",
  herblore:     "Petals become potions become power.",
  runecrafting: "The lattice remembers your patience.",
  agility:      "Traverse first, arrive second, live longest.",
  thieving:     "The market has hidden doors.",
  slayer:       "A ledger of monsters, paid in XP.",
  construction: "A home is a slow, stubborn kind of hope.",
};

function Card({ title, right, children, className = "", testid }) {
  return (
    <section className={"panel p-3 " + className} data-testid={testid}>
      {title && (
        <header className="flex items-center justify-between mb-2">
          <h3 className="uppercase tracking-widest text-[10px] font-numeric text-amber-500">{title}</h3>
          {right}
        </header>
      )}
      {children}
    </section>
  );
}

function IngredientChip({ id, qty, contentItems, positive = false }) {
  const name = itemName(id, contentItems);
  const icon = ITEM_ICON[id];
  return (
    <span
      className={
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-numeric text-[10px] " +
        (positive
          ? "bg-emerald-900/30 border-emerald-800 text-emerald-200"
          : "bg-stone-900 border-stone-700 text-stone-300")
      }
      title={name}
    >
      {icon ? <span className="text-sm leading-none">{icon}</span> : null}
      <span>{qty}× {name}</span>
    </span>
  );
}

function ActionRow({ act, skillLevel, active, contentItems, onStart, onStop }) {
  const locked = skillLevel < act.level;
  const isActive = active?.id === act.id;
  const cost = act.cost || [];
  const yields = act.yields || [];
  const seconds = ((act.duration_ticks || 1) * 0.6).toFixed(1);

  return (
    <div
      className={
        "panel-inset p-3 flex flex-col gap-2 transition-colors " +
        (isActive ? "border-amber-500 pulse-tick" : locked ? "opacity-60" : "")
      }
      data-testid={`skill-action-${act.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display text-sm text-stone-100">{act.name}</span>
            <span className="text-[9px] font-numeric text-stone-500 bg-stone-900 border border-stone-700 rounded px-1.5 py-0.5 uppercase tracking-widest">
              LV {act.level}
            </span>
          </div>
          <div className="text-[10px] font-numeric text-stone-500 mt-0.5">
            +{act.xp} XP · every {seconds}s{act.burn_stop_level ? ` · stops burning at LV ${act.burn_stop_level}` : ""}
          </div>
        </div>
        {isActive ? (
          <button
            onClick={onStop}
            className="btn-ghost-gold text-[10px] px-2 py-1 flex items-center gap-1 shrink-0"
            data-testid={`stop-${act.id}`}
          >
            <StopCircle size={12} /> Stop
          </button>
        ) : (
          <button
            onClick={() => onStart(act.id)}
            disabled={locked}
            data-testid={`start-${act.id}`}
            className={
              "text-[10px] px-2 py-1 flex items-center gap-1 shrink-0 rounded border transition-colors " +
              (locked
                ? "bg-stone-900 border-stone-800 text-stone-600 cursor-not-allowed"
                : "bg-amber-800 hover:bg-amber-700 border-amber-600 text-amber-50")
            }
          >
            <Play size={12} /> Train
          </button>
        )}
      </div>

      {(cost.length > 0 || yields.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
          {cost.map(([id, qty]) => (
            <IngredientChip key={"c-" + id} id={id} qty={qty} contentItems={contentItems} />
          ))}
          {cost.length > 0 && yields.length > 0 && (
            <ChevronRight size={12} className="text-stone-600 mx-0.5" />
          )}
          {yields.map(([id, qty], idx) => (
            <IngredientChip key={"y-" + id + idx} id={id} qty={qty} contentItems={contentItems} positive />
          ))}
        </div>
      )}

      {locked && (
        <div className="text-[10px] font-body text-stone-500 italic">
          Unlocks at Lv. {act.level} — {act.level - skillLevel} to go.
        </div>
      )}
    </div>
  );
}

function MilestoneBar({ skillData }) {
  const level = skillData?.level ?? 1;
  const milestones = [10, 25, 50, 75, 99];
  return (
    <div>
      <div className="flex items-center justify-between mb-1 text-[10px] font-numeric text-stone-500 uppercase tracking-widest">
        <span>Progress to LV 99</span>
        <span className="text-amber-400">LV {level} / 99</span>
      </div>
      <div className="relative xp-bar-outer" style={{ height: 8 }}>
        <div className="xp-bar-fill" style={{ width: `${Math.min(100, (level / 99) * 100)}%` }} />
        {milestones.map((m) => {
          const reached = level >= m;
          return (
            <div
              key={m}
              className="absolute top-1/2 -translate-y-1/2"
              style={{ left: `calc(${(m / 99) * 100}% - 4px)` }}
              title={`Milestone LV ${m}`}
            >
              <div
                className={
                  "w-2 h-2 rounded-full border " +
                  (reached
                    ? "bg-amber-400 border-amber-200"
                    : "bg-stone-950 border-stone-600")
                }
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1 text-[9px] font-numeric text-stone-600">
        {milestones.map((m) => (
          <span key={m} className={level >= m ? "text-amber-400" : ""}>{m}</span>
        ))}
      </div>
    </div>
  );
}

export function SkillPage({ skillName, onBack }) {
  const { snap, content, startAction, stopAction } = useGame();
  const [showLocked, setShowLocked] = useState(true);

  const meta = content?.skill_meta?.[skillName] || null;
  const skillLabel = meta?.label || SKILL_LABEL[skillName] || skillName;
  const skillTagline = meta?.tagline || SKILL_TAGLINE[skillName];

  const skillData = { __name: skillName, ...(snap?.skills?.[skillName] || {}) };
  const contentItems = content?.items || {};
  const actionsMap = content?.actions || {};
  const active = snap?.action;

  const actions = useMemo(() => {
    return Object.entries(actionsMap)
      .filter(([, a]) => a.skill === skillName)
      .map(([id, a]) => ({ id, ...a }))
      .sort((a, b) => a.level - b.level);
  }, [actionsMap, skillName]);

  const category = useMemo(() => {
    if (!content?.skills) return "gathering";
    for (const [cat, list] of Object.entries(content.skills)) {
      if (list.includes(skillName)) return cat;
    }
    return "gathering";
  }, [content, skillName]);

  const categoryLabel = meta?.category_label || CATEGORY_LABEL[category] || category;
  const overrideIconUrl = meta?.icon_url ? (meta.icon_url.startsWith("http") ? meta.icon_url : meta.icon_url) : null;
  const overrideHeroUrl = meta?.hero_url ? (meta.hero_url.startsWith("http") ? meta.hero_url : meta.hero_url) : null;

  const iconUrl = overrideIconUrl || skillIcon(skillName);
  const unlocked = actions.filter((a) => a.level <= (skillData.level ?? 1));
  const locked   = actions.filter((a) => a.level >  (skillData.level ?? 1));
  const bestUnlocked = unlocked.length ? unlocked[unlocked.length - 1] : null;
  const nextLocked   = locked.length ? locked[0] : null;

  const pctToNext = pctInLevel(skillData);
  const xpTotal = skillData.xp ?? 0;
  const xpInto = skillData.xp_into ?? 0;
  const xpNeeded = skillData.xp_needed ?? 0;

  // Pet unlock: matching id from content.pets
  const petEntry = useMemo(() => {
    if (!content?.pets) return null;
    for (const [id, pet] of Object.entries(content.pets)) {
      if (pet.skill === skillName) return { id, ...pet };
    }
    return null;
  }, [content, skillName]);

  async function onStart(actId) {
    try { await startAction(actId); toast.success("Started."); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function onStop() {
    try { await stopAction(); toast.message("Idle."); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  // Ambient background: painted asset for the skill if present, else default.
  const heroImg = overrideHeroUrl || iconUrl || "/generated/hero_landing.png";

  return (
    <div className="p-4 lg:p-6 space-y-3" data-testid={`skill-page-${skillName}`}>
      {/* Tabs (Actions / Mastery / Rewards — placeholders match app pattern) */}
      <div className="flex items-center gap-2 text-xs font-body px-1">
        <button className="text-amber-400 border-b-2 border-amber-500 pb-1 px-1" data-testid="tab-actions">Actions</button>
        <button className="text-stone-500 hover:text-amber-500 pb-1 px-1" title="Coming soon">Mastery</button>
        <button className="text-stone-500 hover:text-amber-500 pb-1 px-1" title="Coming soon">Rewards</button>
        <div className="flex-1" />
        <button
          onClick={onBack}
          className="text-stone-500 hover:text-amber-400 flex items-center gap-1 text-[11px] font-body"
          data-testid="skill-back"
        >
          <ArrowLeft size={12} /> All Skills
        </button>
      </div>

      {/* Portrait band */}
      <div
        className="panel overflow-hidden relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.55), rgba(12,10,9,0.92)), url(${heroImg})`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="p-6 flex items-center gap-6">
          <div className="w-24 h-24 rounded-lg border-2 border-amber-700 bg-stone-950 flex items-center justify-center overflow-hidden">
            {iconUrl ? (
              <img src={iconUrl} alt={skillLabel} className="w-full h-full object-cover" />
            ) : (
              <span className="font-display text-3xl text-amber-400">
                {skillLabel?.slice(0, 1)}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-4xl heading-gilded truncate" data-testid={`skill-title-${skillName}`}>
              {skillLabel}
            </h1>
            <div className="text-stone-400 font-body italic text-sm truncate">
              {skillTagline}
            </div>
            <div className="flex flex-wrap gap-4 mt-2 text-sm font-numeric text-stone-300">
              <span>Lv. <span className="text-amber-300">{skillData.level ?? 1}</span> / 99</span>
              <span>Total XP · <span className="text-amber-300">{(xpTotal || 0).toLocaleString()}</span></span>
              <span>Category · <span className="text-amber-300">{categoryLabel}</span></span>
            </div>
          </div>
          <div className="hidden lg:block">
            <SkillRing skill={skillData} size={92} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        {/* Left column: Info + Best/Next action + Milestones */}
        <div className="lg:col-span-3 space-y-3">
          <Card title="Information" testid="skill-info-card">
            <dl className="text-xs font-body space-y-1.5">
              <div className="flex justify-between">
                <dt className="text-stone-500">Level</dt>
                <dd className="text-amber-300 font-numeric">{skillData.level ?? 1}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-stone-500">Total XP</dt>
                <dd className="text-amber-300 font-numeric">{(xpTotal || 0).toLocaleString()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-stone-500">To next Lv.</dt>
                <dd className="text-amber-300 font-numeric">
                  {xpNeeded ? `${xpInto}/${xpNeeded}` : "MAX"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-stone-500">Actions unlocked</dt>
                <dd className="text-amber-300 font-numeric">{unlocked.length} / {actions.length}</dd>
              </div>
            </dl>
            {xpNeeded > 0 && (
              <div className="mt-2 xp-bar-outer">
                <div className="xp-bar-fill" style={{ width: `${pctToNext}%` }} data-testid="skill-xp-fill" />
              </div>
            )}
          </Card>

          {bestUnlocked && (
            <Card title="Best Available" testid="best-available-card">
              <div className="flex items-center gap-2">
                <Trophy size={14} className="text-amber-400 shrink-0" />
                <div className="min-w-0">
                  <div className="font-display text-sm truncate">{bestUnlocked.name}</div>
                  <div className="text-[10px] font-numeric text-stone-500">
                    +{bestUnlocked.xp} XP · every {(bestUnlocked.duration_ticks * 0.6).toFixed(1)}s
                  </div>
                </div>
              </div>
              <button
                onClick={() => onStart(bestUnlocked.id)}
                className="btn-gold w-full py-1 text-xs mt-2"
                data-testid="best-available-start"
              >
                Train now
              </button>
            </Card>
          )}

          {nextLocked && (
            <Card title="Coming Up" testid="next-unlock-card">
              <div className="flex items-center gap-2">
                <ScrollText size={14} className="text-stone-500 shrink-0" />
                <div className="min-w-0">
                  <div className="font-display text-sm truncate">{nextLocked.name}</div>
                  <div className="text-[10px] font-numeric text-stone-500">
                    Unlocks at Lv. {nextLocked.level} — {nextLocked.level - (skillData.level ?? 1)} to go
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Center: Actions */}
        <div className="lg:col-span-6 space-y-3">
          <Card
            title={`Actions (${unlocked.length}/${actions.length})`}
            testid="skill-actions-card"
            right={
              actions.length > 0 && (
                <span className="text-[10px] font-numeric text-stone-500">
                  {active ? `Training: ${active.id}` : "Idle"}
                </span>
              )
            }
          >
            {actions.length === 0 ? (
              <div className="text-stone-500 italic text-xs text-center py-6 font-body">
                Actions for {SKILL_LABEL[skillName]} arrive in a future chapter.
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {unlocked.slice().reverse().map((act) => (
                    <ActionRow
                      key={act.id}
                      act={act}
                      skillLevel={skillData.level ?? 1}
                      active={active}
                      contentItems={contentItems}
                      onStart={onStart}
                      onStop={onStop}
                    />
                  ))}
                </div>
                {locked.length > 0 && (
                  <div className="mt-3">
                    <button
                      onClick={() => setShowLocked((v) => !v)}
                      className="w-full flex items-center justify-between px-2 py-1.5 rounded border border-stone-800 text-[10px] uppercase tracking-widest font-numeric text-stone-500 hover:text-amber-400"
                      data-testid="toggle-locked"
                    >
                      <span>Locked ({locked.length})</span>
                      <ChevronDown size={12} className={showLocked ? "" : "-rotate-90"} />
                    </button>
                    {showLocked && (
                      <div className="space-y-2 mt-2">
                        {locked.map((act) => (
                          <ActionRow
                            key={act.id}
                            act={act}
                            skillLevel={skillData.level ?? 1}
                            active={active}
                            contentItems={contentItems}
                            onStart={onStart}
                            onStop={onStop}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </Card>
        </div>

        {/* Right: Pet + Notable Items */}
        <div className="lg:col-span-3 space-y-3">
          <Card title="Companion" testid="skill-pet-card" right={<PawPrint size={12} className="text-amber-500" />}>
            {petEntry ? (
              <div className="flex items-center gap-3">
                <img
                  src={`/generated/pet_${petEntry.id}.png`}
                  alt={petEntry.name}
                  className="w-14 h-14 rounded-full border-2 border-amber-700 object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div className="min-w-0">
                  <div className="font-display text-sm truncate">{petEntry.name}</div>
                  <div className="text-[10px] font-numeric text-stone-500">
                    +{Math.round((petEntry.buff || 0) * 100)}% XP when active
                  </div>
                  <div className="text-[10px] italic text-stone-500 mt-0.5">
                    Rare drop from training.
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-stone-500 italic text-xs text-center py-2 font-body">
                No known companion is drawn to this discipline.
              </div>
            )}
          </Card>

          <Card title="Notable Yields" testid="skill-yields-card" right={<Package size={12} className="text-amber-500" />}>
            <NotableYields actions={actions} contentItems={contentItems} />
          </Card>

          <Card title="Milestones" testid="skill-milestones-card">
            <MilestoneBar skillData={skillData} />
          </Card>
        </div>

        {/* Full width: tips */}
        <div className="lg:col-span-12">
          <Card title="Practitioner's Notes" testid="skill-notes">
            <TipsRow skill={skillName} category={category} />
          </Card>
        </div>
      </div>
    </div>
  );
}

function NotableYields({ actions, contentItems }) {
  const items = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const a of actions) {
      for (const [id] of a.yields || []) {
        if (seen.has(id)) continue;
        seen.add(id);
        list.push(id);
        if (list.length >= 6) return list;
      }
    }
    return list;
  }, [actions]);
  if (items.length === 0) {
    return <div className="text-stone-500 italic text-xs">Yields are earned in combat, not from actions.</div>;
  }
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {items.map((id) => (
        <div key={id} className="inv-slot" title={itemName(id, contentItems)}>
          <span className="text-2xl">{ITEM_ICON[id] || "◇"}</span>
        </div>
      ))}
    </div>
  );
}

function TipsRow({ skill, category }) {
  const tips = {
    gathering: [
      { icon: Flame,    label: "Idle-safe",     desc: "Runs happily offline. Cap 24h — check back tomorrow." },
      { icon: Sparkles, label: "Pet chance",    desc: "Every successful yield has a very small pet roll." },
      { icon: Coins,    label: "Grand Exchange", desc: "Raw yields sell fast to artisans. Tax is a gold sink." },
    ],
    artisan: [
      { icon: Package,  label: "Inputs matter", desc: "Actions consume from your Inventory, not Bank." },
      { icon: Flame,    label: "Cook carefully", desc: "Cooking may burn until the skill's burn-stop level." },
      { icon: Coins,    label: "Bar to blade",  desc: "Refined items fetch far better GE prices than raw." },
    ],
    support: [
      { icon: Sparkles, label: "Enablers",       desc: "Support skills quietly unlock the rest of the world." },
      { icon: Flame,    label: "Long-tail XP",   desc: "Rewards scale slowly but compound with content." },
    ],
    combat: [
      { icon: Sparkles, label: "Train in Arena", desc: "Combat skills level up through fighting, not idle actions." },
    ],
  };
  const list = tips[category] || tips.gathering;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {list.map((t, i) => {
        const Icon = t.icon;
        return (
          <div key={i} className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-900/30 border border-amber-700 flex items-center justify-center text-amber-400 shrink-0">
              <Icon size={14} />
            </div>
            <div>
              <div className="text-sm font-body text-stone-200">{t.label}</div>
              <div className="text-[11px] text-stone-500 italic">{t.desc}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
